import React, { memo } from "react";
import { Box, Flex, Switch, Text, Collapse } from "@chakra-ui/react";
import { ToggleBlockProps } from "../types";

/**
 * ToggleBlock - A reusable component for collapsible content with a toggle switch
 * Used in EventPreferencesStep for Block 1 (Insight) and Block 2 (Alert)
 *
 * Pattern: Chakra UI Switch + Collapse for smooth expand/collapse animation
 */
export const ToggleBlock: React.FC<ToggleBlockProps> = memo(
  ({
    title,
    isActive,
    onToggle,
    badge,
    icon,
    children,
    isDisabled = false,
    hideSwitch = false,
  }) => {
    // When hideSwitch is true, content is always visible
    const isExpanded = hideSwitch ? true : isActive;

    return (
      <Box
        borderWidth="1px"
        borderRadius="md"
        borderColor="#D4D4D8"
        p={4}
        bg="white"
        transition="all 0.2s ease"
        opacity={isDisabled ? 0.6 : 1}
      >
        <Flex
          justify="space-between"
          align="center"
          mb={isExpanded ? 4 : 0}
          cursor={isDisabled || hideSwitch ? "default" : "pointer"}
          onClick={(e) => {
            // Don't toggle if hideSwitch is true
            if (hideSwitch) return;
            // Prevent toggling if the click is on the Switch itself
            if ((e.target as HTMLElement).closest(".chakra-switch")) {
              return;
            }
            if (!isDisabled) {
              onToggle(!isActive);
            }
          }}
        >
          <Flex align="center" gap={4}>
            {icon && <Box flexShrink={0}>{icon}</Box>}
            <Text
              fontWeight="400"
              color="#086AA9"
              fontSize="md"
              lineHeight="24px"
            >
              {title}
            </Text>
            {badge &&
              (typeof badge === "string" ? (
                <Box
                  bg="#F7F0FD"
                  color="#7F17DE"
                  px={2}
                  py="2px"
                  borderRadius="4px"
                  fontSize="14px"
                  fontWeight="400"
                  lineHeight="20px"
                  minH="24px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {badge}
                </Box>
              ) : (
                badge
              ))}
          </Flex>
          {!hideSwitch && (
            <Switch
              isChecked={isActive}
              onChange={() => {
                if (!isDisabled) {
                  onToggle(!isActive);
                }
              }}
              colorScheme="blue"
              size="md"
              isDisabled={isDisabled}
            />
          )}
        </Flex>
        <Collapse in={isExpanded} animateOpacity>
          <Box pt={2}>{children}</Box>
        </Collapse>
      </Box>
    );
  },
);

ToggleBlock.displayName = "ToggleBlock";

export default ToggleBlock;
